import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getTenantSlug } from '../allauth/lib/django';
import { useNativeWebViewInsets } from '../context/NativeWebViewInsetsContext';
import { parseNativeAppOS } from '../utils/nativeWebViewInsets';
import {
  buildEnvTenantHomeUrl,
  TenantDeployEnv,
} from '../utils/tenantUrl';

const ENV_PICKER_DONE_KEY = 'nativeEnvPickerDone';
const ENV_PICKER_QUERY = 'envPicker';

function readAppOS() {
  if (typeof window === 'undefined') return undefined;
  const fromUrl = parseNativeAppOS(new URLSearchParams(window.location.search).get('appOS'));
  if (fromUrl) return fromUrl;
  try {
    return parseNativeAppOS(window.localStorage.getItem('appOS'));
  } catch {
    return undefined;
  }
}

function isPickerDone(): boolean {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get(ENV_PICKER_QUERY) === '1') {
    try {
      window.sessionStorage.setItem(ENV_PICKER_DONE_KEY, '1');
    } catch {
      /* ignore */
    }
    return true;
  }
  try {
    return window.sessionStorage.getItem(ENV_PICKER_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function markPickerDone(): void {
  try {
    window.sessionStorage.setItem(ENV_PICKER_DONE_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** True when the native WebView should show the env picker. */
export function shouldShowNativeEnvTenantPicker(isNativeWebView: boolean): boolean {
  if (!isNativeWebView && !readAppOS()) return false;
  return !isPickerDone();
}

const NativeEnvTenantPickerDialog: React.FC = () => {
  const { isNativeWebView, appOS } = useNativeWebViewInsets();
  const [open, setOpen] = useState(false);
  const [env, setEnv] = useState<TenantDeployEnv>('stage');

  const resolvedAppOS = appOS || readAppOS();

  useEffect(() => {
    setOpen(shouldShowNativeEnvTenantPicker(isNativeWebView || !!resolvedAppOS));
  }, [isNativeWebView, resolvedAppOS]);

  const handleContinue = () => {
    markPickerDone();

    const targetUrl = buildEnvTenantHomeUrl(getTenantSlug(), env, {
      appOS: resolvedAppOS,
      [ENV_PICKER_QUERY]: '1',
    });

    let targetHost: string;
    try {
      targetHost = new URL(targetUrl).host;
    } catch {
      targetHost = '';
    }

    if (targetHost && targetHost === window.location.host) {
      setOpen(false);
      return;
    }

    window.location.assign(targetUrl);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      aria-labelledby="native-env-tenant-picker-title"
    >
      <DialogTitle id="native-env-tenant-picker-title">
        Choose environment
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick Stage or Production. Your current organization will be kept.
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={env}
          onChange={(_e, next: TenantDeployEnv | null) => {
            if (next) setEnv(next);
          }}
        >
          <ToggleButton value="stage">Stage</ToggleButton>
          <ToggleButton value="production">Production</ToggleButton>
        </ToggleButtonGroup>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          data-testid="native-env-tenant-continue"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NativeEnvTenantPickerDialog;
